<template>
  <div class="order-shipping mt-4">
    <div class="d-flex align-items-center mb-3">
      <h6 class="font-weight-bold mb-0 mr-2">
        <i class="fas fa-truck text-primary mr-1"></i> Vận chuyển
      </h6>
      <b-badge v-if="shippingInfo" :variant="statusVariant" class="px-2">
        {{ statusName }}
      </b-badge>
    </div>

    <!-- Chưa có đơn vận chuyển -->
    <div v-if="!shippingInfo && !isLoading" class="text-center py-3 border rounded bg-light">
      <p class="text-muted mb-2">Chưa tạo đơn vận chuyển</p>
      <b-button
        size="sm"
        variant="primary"
        @click="openCreateModal"
        :disabled="!canCreate"
      >
        <i class="fas fa-plus mr-1"></i> Tạo đơn vận chuyển
      </b-button>
      <p v-if="!canCreate" class="text-muted mt-1" style="font-size:12px">
        Đơn hàng cần có tên, SĐT, địa chỉ người nhận
      </p>
    </div>

    <!-- Đang tải -->
    <div v-if="isLoading" class="text-center py-3">
      <b-spinner small variant="primary" />
    </div>

    <!-- Đã có đơn vận chuyển -->
    <div v-if="shippingInfo && !isLoading" class="border rounded p-3">
      <b-row>
        <b-col cols="6">
          <!-- Nhà vận chuyển -->
          <div class="mb-2">
            <span class="text-muted" style="font-size:12px">Nhà vận chuyển</span><br />
            <strong>{{ carrierLabel }}</strong>
          </div>
          <!-- Mã vận đơn -->
          <div class="mb-2">
            <span class="text-muted" style="font-size:12px">Mã vận đơn</span><br />
            <strong class="text-primary">{{ shippingInfo.trackingCode }}</strong>
            <b-button
              v-if="shippingInfo.trackingCode && shippingInfo.trackingCode !== 'PENDING'"
              size="sm"
              variant="link"
              class="p-0 ml-1"
              @click="copyTrackingCode"
            >
              <i class="far fa-copy"></i>
            </b-button>
          </div>
          <!-- Địa điểm gửi -->
          <div class="mb-2" v-if="shippingInfo.senderName || shippingInfo.senderAddress">
            <span class="text-muted" style="font-size:12px">Địa điểm gửi</span><br />
            <span v-if="shippingInfo.senderName"><strong>{{ shippingInfo.senderName }}</strong></span><br />
            <span v-if="shippingInfo.senderPhone" class="text-muted ml-1" style="font-size:12px">{{ shippingInfo.senderPhone }}</span>
            <div v-if="shippingInfo.senderAddress" class="text-muted" style="font-size:12px"> <i class="fas fa-map-marker-alt text-danger mr-1"></i> {{ shippingInfo.senderAddress }}</div>
          </div>
        </b-col>
        <b-col cols="6">
          <!-- Cước phí -->
          <div class="mb-2" v-if="shippingInfo.totalFee">
            <span class="text-muted" style="font-size:12px">Cước phí</span><br />
            <strong>{{ formatCurrency(shippingInfo.totalFee) }}</strong>
          </div>
          <!-- Dự kiến giao -->
          <div class="mb-2">
            <span class="text-muted" style="font-size:12px">Dự kiến giao</span><br />
            <strong>{{ formatDate(shippingInfo.expectedDeliveryAt) || '--' }}</strong>
          </div>
          <!-- Địa điểm nhận -->
          <div class="mb-2" v-if="shippingInfo.receiverName || shippingInfo.receiverAddress">
            <span class="text-muted" style="font-size:12px">Địa điểm nhận</span><br />
            <span v-if="shippingInfo.receiverName"><strong>{{ shippingInfo.receiverName }}</strong></span><br />
            <span v-if="shippingInfo.receiverPhone" class="text-muted ml-1" style="font-size:12px">{{ shippingInfo.receiverPhone }}</span>
            <div v-if="shippingInfo.receiverAddress" class="text-muted" style="font-size:12px"> <i class="fas fa-map-marker-alt text-danger mr-1"></i> {{ shippingInfo.receiverAddress }}</div>
          </div>

          <!-- Hình thức thanh toán -->
          <div class="mt-2" v-if="paymentMethodName">
            <span class="text-muted" style="font-size:12px">Hình thức thanh toán</span><br />
            <strong>{{ paymentMethodName }}</strong>
          </div>
        </b-col>
      </b-row>

      <!-- Actions -->
      <div class="mt-2 d-flex" style="gap: 8px">
        <b-dropdown
          v-if="shippingInfo && shippingInfo.carrier === 'viettelpost'"
          size="sm"
          variant="outline-secondary"
          :disabled="!canPrint"
          no-caret
          split
          @click="printLabel('1')"
        >
          <template #button-content>
            <i class="fas fa-print mr-1"></i> In nhãn A5
          </template>
          <b-dropdown-item @click="printLabel('1')">Nhãn A5</b-dropdown-item>
          <b-dropdown-item @click="printLabel('2')">Nhãn A6</b-dropdown-item>
          <b-dropdown-item @click="printLabel('100')">Nhãn A7</b-dropdown-item>
        </b-dropdown>
        <b-button
          v-else
          size="sm"
          variant="outline-secondary"
          @click="printLabel('1')"
          :disabled="!canPrint"
        >
          <i class="fas fa-print mr-1"></i> In nhãn
        </b-button>
        <b-button
          size="sm"
          variant="outline-danger"
          @click="confirmCancel"
          v-if="canCancel"
        >
          <i class="fas fa-times mr-1"></i> Hủy đơn vận chuyển
        </b-button>
      </div>
    </div>

    <!-- Modal tạo đơn -->
    <ShippingOrderModal
      ref="shippingModal"
      :orderId="orderId"
      :orderInfo="orderInfo"
      @shipping-created="onShippingCreated"
    />
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import ShippingOrderModal from '@/view/components/shipping/ShippingOrderModal.vue';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { VTP_PAYMENT_METHOD_NAME, NT_PAYMENT_METHOD_NAME } from '@/utils/enum-name';

const ShippingRepo = RepositoryFactory.get('shipping');

const CARRIER_LABELS = {
  viettelpost: 'ViettelPost',
  nhattin: 'Nhất Tín Logistics',
};

const PAYMENT_METHOD_NAMES = {
  viettelpost: VTP_PAYMENT_METHOD_NAME,
  nhattin: NT_PAYMENT_METHOD_NAME,
};

const CANCELLABLE_STATUSES = ['CREATED', 'PICKING', 'PENDING'];

const STATUS_VARIANTS = {
  PENDING: 'warning',
  CREATED: 'info',
  PICKING: 'primary',
  PICKED: 'primary',
  DELIVERING: 'primary',
  DELIVERED: 'success',
  FAILED: 'danger',
  CANCELLED: 'secondary',
  RETURNED: 'secondary',
};

const STATUS_NAMES = {
  PENDING: 'Chờ xử lý',
  CREATED: 'Đã tạo',
  PICKING: 'Đang lấy hàng',
  PICKED: 'Đã lấy hàng',
  DELIVERING: 'Đang giao',
  DELIVERED: 'Đã giao',
  FAILED: 'Giao thất bại',
  CANCELLED: 'Đã hủy',
  RETURNED: 'Hoàn hàng',
};

export default {
  name: 'OrderShipping',
  components: { ShippingOrderModal },
  props: {
    orderId: { type: Number, required: true },
    orderInfo: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      shippingInfo: null,
      isLoading: false,
    };
  },
  computed: {
    carrierLabel() {
      return CARRIER_LABELS[this.shippingInfo?.carrier] || this.shippingInfo?.carrier;
    },
    statusVariant() {
      return STATUS_VARIANTS[this.shippingInfo?.status] || 'secondary';
    },
    statusName() {
      return STATUS_NAMES[this.shippingInfo?.status] || this.shippingInfo?.status || '';
    },
    paymentMethodName() {
      const carrier = this.shippingInfo?.carrier;
      const method = this.shippingInfo?.paymentMethod;
      if (!carrier || method == null) return '';
      return PAYMENT_METHOD_NAMES[carrier]?.[method] || `#${method}`;
    },
    canCreate() {
      return !!(
        this.orderInfo?.customerName &&
        this.orderInfo?.customerMobile &&
        this.orderInfo?.customerAddress
      );
    },
    canCancel() {
      return (
        this.shippingInfo &&
        CANCELLABLE_STATUSES.includes(this.shippingInfo.status) &&
        this.shippingInfo.status !== 'CANCELLED'
      );
    },
    canPrint() {
      return (
        this.shippingInfo?.trackingCode &&
        this.shippingInfo.trackingCode !== 'PENDING' &&
        this.shippingInfo.status !== 'CANCELLED'
      );
    },
  },
  mounted() {
    if (this.orderId) this.fetchShippingInfo();
  },
  methods: {
    async fetchShippingInfo() {
      this.isLoading = true;
      try {
        this.shippingInfo = await ShippingRepo.getShippingInfo(this.orderId);
      } catch {
        this.shippingInfo = null;
      } finally {
        this.isLoading = false;
      }
    },

    openCreateModal() {
      this.$refs.shippingModal.open();
    },

    onShippingCreated(result) {
      this.fetchShippingInfo();
      makeToastSuccess(`Tạo đơn thành công! Mã vận đơn: ${result.trackingCode}`);
      this.$emit('shipping-created', result);
    },

    async printLabel() {
      try {
        const { url, html } = await ShippingRepo.getPrintLabel(this.orderId);
        if (html) {
          const win = window.open('', '_blank');
          win.document.open();
          win.document.write(html);
          win.document.close();
          win.focus();
          setTimeout(() => win.print(), 800);
        } else {
          const win = window.open(url, '_blank');
          if (win) {
            win.focus();
            // VTP renders via JS client-side, wait for rendering before triggering print
            setTimeout(() => { try { win.print(); } catch (_) {} }, 2500);
          }
        }
      } catch (err) {
        makeToastFaile('Không lấy được link in nhãn');
      }
    },

    confirmCancel() {
      this.$bvModal
        .msgBoxConfirm(
          'Bạn có chắc chắn muốn hủy đơn vận chuyển này không? Trạng thái vận chuyển đơn hàng sẽ được khôi phục về trước đó.',
          {
            title: 'Xác nhận hủy đơn vận chuyển',
            okTitle: 'Hủy đơn',
            cancelTitle: 'Quay lại',
            okVariant: 'danger',
            centered: true,
          }
        )
        .then((confirmed) => {
          if (confirmed) this.doCancel();
        });
    },

    async doCancel() {
      try {
        await ShippingRepo.cancelShipping(this.orderId);
        makeToastSuccess('Đã hủy đơn vận chuyển');
        await this.fetchShippingInfo();
        this.$emit('shipping-cancelled');
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Hủy đơn thất bại');
      }
    },

    copyTrackingCode() {
      navigator.clipboard.writeText(this.shippingInfo.trackingCode).then(() => {
        makeToastSuccess('Đã copy mã vận đơn');
      });
    },

    formatCurrency(value) {
      if (!value) return '0 ₫';
      return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('vi-VN');
    },
  },
};
</script>
